/**
 * Share Target handler - receives files shared from DJI Fly, DJI Pilot,
 * DJI Pilot 2 or any Android app via the system "Compartilhar" menu.
 *
 * The PWA manifest declares share_target so Android OS lists FlightSync as
 * a share destination. When the user picks it, Android POSTs the file to
 * /flightsync/share, which this component handles.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { parseDjiLog } from '../../lib/djiLogParser';
import { addFlight, pruneOldSynced } from '../../lib/flightSyncDb';
import { flushQueue } from '../../lib/flightSyncApi';

export default function SharePage({ ctx }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [result, setResult] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      try {
        // Register the SW message listener FIRST (before asking SW for payload)
        // to avoid race conditions where SW replies synchronously.
        const files = await new Promise((resolve) => {
          let resolved = false;
          const finish = (result) => {
            if (resolved) return;
            resolved = true;
            resolve(result);
          };
          const timeout = setTimeout(() => finish([]), 4000);
          const listener = (ev) => {
            if (ev.data?.action === 'shared-file' && Array.isArray(ev.data.files)) {
              clearTimeout(timeout);
              navigator.serviceWorker?.removeEventListener('message', listener);
              finish(ev.data.files);
            }
          };
          navigator.serviceWorker?.addEventListener('message', listener);
          // Now ask the SW for the cached shared payload
          navigator.serviceWorker?.controller?.postMessage({ action: 'get-shared-files' });
        });

        if (!files || files.length === 0) {
          setStatus('empty');
          return;
        }
        let ok = 0, fail = 0;
        for (const f of files) {
          try {
            const parsed = await parseDjiLog(f);
            const flight = {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              ...parsed,
              pilot_name: ctx.pilot_name,
              drone_model: parsed.drone_model || ctx.drone_model,
              drone_serial: parsed.drone_serial || ctx.drone_serial,
              synced: false,
              created_at: new Date().toISOString(),
              notes: `[share-target] ${f.name}`,
            };
            await addFlight(flight);
            ok++;
          } catch (e) {
            fail++;
            // eslint-disable-next-line no-console
            console.warn('[share] parse failed', f.name, e);
          }
        }
        await pruneOldSynced();
        if (navigator.onLine && ctx.token) flushQueue(ctx.token);
        setResult({ ok, fail, files: files.map((f) => f.name) });
        setStatus(ok > 0 ? 'success' : 'error');
        if (ok > 0) toast.success(`${ok} voo(s) importado(s) do compartilhamento`);
      } catch (e) {
        setStatus('error');
        setResult({ error: String(e.message || e) });
      }
    };
    run();
  }, [ctx]);

  return (
    <div data-testid="fs-share-page">
      <div className="fs-hero">
        <div className="fs-hero-label">RECEBIDO VIA COMPARTILHAR</div>
        <div className="fs-hero-value" style={{ fontSize: 22 }}>Importacao Rapida</div>
      </div>

      <div className="fs-card" style={{ textAlign: 'center', padding: 28 }}>
        {status === 'processing' && (
          <>
            <Loader2 size={40} className="fs-spin" style={{ color: 'var(--fs-cyan)', margin: '0 auto 12px' }} />
            <p style={{ fontSize: 14 }}>Processando arquivo(s)...</p>
          </>
        )}
        {status === 'success' && result && (
          <>
            <CheckCircle2 size={40} style={{ color: 'var(--fs-green)', margin: '0 auto 12px' }} />
            <p style={{ fontSize: 14, fontWeight: 600 }}>{result.ok} voo(s) importado(s)</p>
            {result.fail > 0 && <p style={{ fontSize: 12, color: 'var(--fs-red)' }}>{result.fail} falharam</p>}
            <div style={{ marginTop: 16, textAlign: 'left' }}>
              {result.files?.map((n, i) => (
                <div key={i} className="fs-flight-meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileText size={11} /> {n}
                </div>
              ))}
            </div>
          </>
        )}
        {status === 'empty' && (
          <>
            <FileText size={40} style={{ color: 'var(--fs-text-dim)', margin: '0 auto 12px' }} />
            <p style={{ fontSize: 14 }}>Nenhum arquivo recebido.</p>
            <p style={{ fontSize: 12, color: 'var(--fs-text-dim)', marginTop: 6 }}>
              No DJI Fly/Pilot, toque em Compartilhar e escolha FlightSync.
            </p>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle size={40} style={{ color: 'var(--fs-red)', margin: '0 auto 12px' }} />
            <p style={{ fontSize: 14 }}>Erro ao processar.</p>
            {result?.error && <p style={{ fontSize: 11, color: 'var(--fs-text-dim)' }}>{result.error}</p>}
          </>
        )}
        <button className="fs-btn fs-btn-primary" onClick={() => navigate('/flightsync/flights')} style={{ marginTop: 18, width: '100%' }}>
          VER MEUS VOOS
        </button>
      </div>
    </div>
  );
}
